import { useState } from 'react'
import { Download, ExternalLink, FileText, Plus } from 'lucide-react'
import { toast } from 'sonner'
import { Button, Chip, Drawer, Input, Select, Textarea } from '@/components/ui'
import {
  casePdfUrl,
  useAddCaseLine,
  useCaseDetail,
  useDeleteCaseLine,
  useGenerateCasePdf,
  usePatchCaseHeader,
  usePatchCaseLine,
  useTransitionCase,
} from '../api/queries'
import { CasePhotoGallery } from './CasePhotoGallery'
import type {
  CaseLine,
  ClaimFiledWithType,
  ClaimStatus,
  ResolutionType,
  Stage,
} from '../api/types'
import {
  ALL_CLAIM_FILED_WITH_TYPES,
  ALL_CLAIM_STATUSES,
  ALL_RESOLUTION_TYPES,
  ALL_STAGES,
  CATEGORY_LABELS,
  CLAIM_FILED_WITH_TYPE_LABELS,
  CLAIM_STATUS_LABELS,
  RESOLUTION_LABELS,
  STAGE_LABELS,
  STAGE_VARIANT,
  TERMINAL_STAGES,
  hasClaimContext,
  transitionAllowed,
} from '../utils/stage-meta'
import { CaseLineGrid } from './CaseLineGrid'
import { StageHistoryTimeline } from './StageHistoryTimeline'

/** Free-text/number header fields edited through the draft+onBlur path. */
type TextField =
  | 'claim_amount_filed'
  | 'claim_amount_recovered'
  | 'claim_filed_with_name'
  | 'claim_number'
  | 'bol_number'
  | 'pro_number'
  | 'resolution_notes'

export interface CaseDetailDrawerProps {
  caseId: string | null
  onOpenChange: (open: boolean) => void
}

function formatMoney(value: string | null): string {
  if (!value) return '—'
  const n = Number(value)
  if (Number.isNaN(n)) return '—'
  return n.toLocaleString(undefined, { style: 'currency', currency: 'USD' })
}

export function CaseDetailDrawer({ caseId, onOpenChange }: CaseDetailDrawerProps) {
  const { data: c, isLoading } = useCaseDetail(caseId)
  const patchHeader = usePatchCaseHeader()
  const transition = useTransitionCase()
  const addLine = useAddCaseLine()
  const patchLine = usePatchCaseLine()
  const deleteLine = useDeleteCaseLine()
  const generatePdf = useGenerateCasePdf()

  // One draft map for every free-text field rather than a useState +
  // commitX pair each — there are eight of them now.
  const [drafts, setDrafts] = useState<Partial<Record<TextField, string>>>({})
  const [claimExpanded, setClaimExpanded] = useState(false)

  if (!caseId) return null

  const editable = c ? !TERMINAL_STAGES.has(c.stage) : false
  const availableTargets = c ? ALL_STAGES.filter((s) => transitionAllowed(c.stage, s)) : []
  const showResolutionFields = c
    ? c.stage === 'resolution_pending' || c.stage === 'claim_pending' || TERMINAL_STAGES.has(c.stage)
    : false
  // The Claim section has its own gate, independent of stage and of
  // resolution_type: a carrier claim has no matching resolution_type, and
  // BOL/PRO are known at intake. Cases with no claim context collapse to
  // a one-liner + "Record claim" rather than showing ten empty fields.
  const showClaimFields = c ? hasClaimContext(c) || claimExpanded : false

  function patch(edits: Record<string, unknown>) {
    if (!c) return
    patchHeader.mutate(
      { caseId: c.id, edits },
      {
        onError: (err) =>
          toast.error(`Update failed${err instanceof Error ? `: ${err.message}` : ''}`),
      },
    )
  }

  function draftValue(field: TextField): string {
    if (drafts[field] !== undefined) return drafts[field] as string
    return (c?.[field] as string | null) ?? ''
  }

  function setDraft(field: TextField, value: string) {
    setDrafts((prev) => ({ ...prev, [field]: value }))
  }

  /** Commit on blur, but only when the value actually changed — keeps a
   *  tab-through from firing a PATCH per field. */
  function commitDraft(field: TextField) {
    const draft = drafts[field]
    setDrafts((prev) => {
      const next = { ...prev }
      delete next[field]
      return next
    })
    if (!c || draft === undefined) return
    if (draft === ((c[field] as string | null) ?? '')) return
    patch({ [field]: draft.trim() || null })
  }

  return (
    <Drawer
      open={!!caseId}
      onOpenChange={onOpenChange}
      size="wide"
      ident={c?.case_number ?? '—'}
      title={
        c?.first_product_name
          ? `${c.first_product_name}${c.line_count > 1 ? ` +${c.line_count - 1} more` : ''}`
          : 'Material disposition case'
      }
      meta={
        c ? (
          <div className="flex items-center gap-2">
            <Chip size="xs" variant={STAGE_VARIANT[c.stage]} dot>
              {STAGE_LABELS[c.stage]}
            </Chip>
            <span className="text-role-metadata">{CATEGORY_LABELS[c.category]}</span>
          </div>
        ) : null
      }
      headerActions={
        c ? (
          <div className="flex items-center gap-2">
            {c.has_pdf ? (
              <Button
                variant="secondary"
                size="sm"
                onClick={() => window.open(casePdfUrl(c.id), '_blank')}
              >
                <Download size={14} className="mr-1.5" />
                Download PDF
              </Button>
            ) : null}
            <Button
              variant={c.has_pdf ? 'ghost' : 'secondary'}
              size="sm"
              loading={generatePdf.isPending}
              onClick={() =>
                generatePdf.mutate(c.id, {
                  onSuccess: () => toast.success('Claim PDF generated'),
                  onError: (err) =>
                    toast.error(
                      `PDF generation failed${err instanceof Error ? `: ${err.message}` : ''}`,
                    ),
                })
              }
            >
              <FileText size={14} className="mr-1.5" />
              {c.has_pdf ? 'Regenerate PDF' : 'Generate PDF'}
            </Button>
          </div>
        ) : null
      }
    >
      {isLoading || !c ? (
        <div className="px-6 py-4 text-sm text-tertiary">Loading…</div>
      ) : (
        <div className="flex flex-col gap-6 px-6 py-4">
          <section className="flex flex-col gap-2">
            <label className="flex flex-1 flex-col gap-1 text-sm">
              <span className="text-secondary">Move stage</span>
              <Select
                value=""
                disabled={availableTargets.length === 0 || transition.isPending}
                onChange={(e) => {
                  const target = e.target.value as Stage
                  if (!target) return
                  transition.mutate(
                    { caseId: c.id, targetStage: target },
                    {
                      onSuccess: () => toast.success(`Moved to ${STAGE_LABELS[target]}`),
                      onError: (err) =>
                        toast.error(
                          `Transition failed${err instanceof Error ? `: ${err.message}` : ''}`,
                        ),
                    },
                  )
                }}
              >
                <option value="">Select a stage…</option>
                {availableTargets.map((s) => (
                  <option key={s} value={s}>
                    {STAGE_LABELS[s]}
                  </option>
                ))}
              </Select>
            </label>
            {c.vendor_name ? (
              <div className="text-role-metadata">Vendor: {c.vendor_name}</div>
            ) : null}
            {c.carrier ? (
              <div className="text-role-metadata">Carrier: {c.carrier}</div>
            ) : null}
            {c.location ? (
              <div className="text-role-metadata">Found at: {c.location}</div>
            ) : null}
          </section>

          <section className="flex flex-col gap-2">
            <div className="flex items-center justify-between">
              <h3 className="m-0 text-sm font-semibold text-primary">Products</h3>
              <Button
                variant="ghost"
                size="sm"
                disabled={!editable}
                onClick={() =>
                  addLine.mutate(
                    { caseId: c.id, line: {} },
                    {
                      onError: (err) =>
                        toast.error(
                          `Add product failed${err instanceof Error ? `: ${err.message}` : ''}`,
                        ),
                    },
                  )
                }
              >
                <Plus size={12} /> Add product
              </Button>
            </div>
            <CaseLineGrid
              lines={c.lines}
              disabled={!editable}
              onLineEdit={(lineId, edits) =>
                patchLine.mutate(
                  { caseId: c.id, lineId, edits },
                  {
                    onError: (err) =>
                      toast.error(
                        `Line update failed${err instanceof Error ? `: ${err.message}` : ''}`,
                      ),
                  },
                )
              }
              onLineRemove={(lineId: CaseLine['id']) =>
                deleteLine.mutate(
                  { caseId: c.id, lineId },
                  {
                    onError: (err) =>
                      toast.error(
                        `Remove failed${err instanceof Error ? `: ${err.message}` : ''}`,
                      ),
                  },
                )
              }
            />
            <div className="flex justify-end text-sm">
              <span className="text-secondary">
                Estimated value at risk: <strong>{formatMoney(c.estimated_value)}</strong>
              </span>
            </div>
          </section>

          <section className="flex flex-col gap-2">
            <h3 className="m-0 text-sm font-semibold text-primary">Photos</h3>
            <CasePhotoGallery caseId={c.id} photos={c.photos} disabled={!editable} />
          </section>

          <section className="flex flex-col gap-2">
            <div className="flex items-center justify-between">
              <h3 className="m-0 text-sm font-semibold text-primary">Claim</h3>
              {!showClaimFields && editable ? (
                <Button variant="ghost" size="sm" onClick={() => setClaimExpanded(true)}>
                  <Plus size={12} /> Record claim
                </Button>
              ) : null}
            </div>
            {showClaimFields ? (
              <>
                <div className="flex gap-2">
                  <label className="flex flex-1 flex-col gap-1 text-sm">
                    <span className="text-secondary">Filed with</span>
                    <Select
                      value={c.claim_filed_with_type ?? ''}
                      disabled={!editable}
                      onChange={(e) =>
                        patch({
                          claim_filed_with_type:
                            (e.target.value as ClaimFiledWithType) || null,
                        })
                      }
                    >
                      <option value="">Not yet filed</option>
                      {ALL_CLAIM_FILED_WITH_TYPES.map((t) => (
                        <option key={t} value={t}>
                          {CLAIM_FILED_WITH_TYPE_LABELS[t]}
                        </option>
                      ))}
                    </Select>
                  </label>
                  <label className="flex flex-1 flex-col gap-1 text-sm">
                    <span className="text-secondary">Filed with (name)</span>
                    <Input
                      value={draftValue('claim_filed_with_name')}
                      onChange={(e) => setDraft('claim_filed_with_name', e.target.value)}
                      onBlur={() => commitDraft('claim_filed_with_name')}
                      disabled={!editable}
                      placeholder="e.g. Old Dominion — Cargo Claims"
                    />
                  </label>
                </div>
                <div className="flex gap-2">
                  <label className="flex flex-1 flex-col gap-1 text-sm">
                    <span className="text-secondary">Claim / reference #</span>
                    <Input
                      value={draftValue('claim_number')}
                      onChange={(e) => setDraft('claim_number', e.target.value)}
                      onBlur={() => commitDraft('claim_number')}
                      disabled={!editable}
                      placeholder="carrier's own claim number"
                    />
                  </label>
                  <label className="flex flex-1 flex-col gap-1 text-sm">
                    <span className="text-secondary">Date filed</span>
                    <Input
                      type="date"
                      value={(c.claim_filed_date ?? '').slice(0, 10)}
                      onChange={(e) => patch({ claim_filed_date: e.target.value || null })}
                      disabled={!editable}
                    />
                  </label>
                </div>
                <div className="flex gap-2">
                  <label className="flex flex-1 flex-col gap-1 text-sm">
                    <span className="text-secondary">Claim status</span>
                    <Select
                      value={c.claim_status ?? ''}
                      disabled={!editable}
                      onChange={(e) =>
                        patch({ claim_status: (e.target.value as ClaimStatus) || null })
                      }
                    >
                      <option value="">Not set</option>
                      {ALL_CLAIM_STATUSES.map((st) => (
                        <option key={st} value={st}>
                          {CLAIM_STATUS_LABELS[st]}
                        </option>
                      ))}
                    </Select>
                  </label>
                  <div className="flex-1" />
                </div>
                <div className="flex gap-2">
                  <label className="flex flex-1 flex-col gap-1 text-sm">
                    <span className="text-secondary">BOL number</span>
                    <Input
                      value={draftValue('bol_number')}
                      onChange={(e) => setDraft('bol_number', e.target.value)}
                      onBlur={() => commitDraft('bol_number')}
                      disabled={!editable}
                      placeholder="from the shipping paperwork"
                    />
                  </label>
                  <label className="flex flex-1 flex-col gap-1 text-sm">
                    <span className="text-secondary">PRO number</span>
                    <Input
                      value={draftValue('pro_number')}
                      onChange={(e) => setDraft('pro_number', e.target.value)}
                      onBlur={() => commitDraft('pro_number')}
                      disabled={!editable}
                      placeholder="carrier tracking / PRO"
                    />
                  </label>
                </div>
                <div className="flex gap-2">
                  <label className="flex flex-1 flex-col gap-1 text-sm">
                    <span className="text-secondary">Claim amount filed</span>
                    <Input
                      value={draftValue('claim_amount_filed')}
                      onChange={(e) => setDraft('claim_amount_filed', e.target.value)}
                      onBlur={() => commitDraft('claim_amount_filed')}
                      disabled={!editable}
                      placeholder="0.00"
                    />
                  </label>
                  <label className="flex flex-1 flex-col gap-1 text-sm">
                    <span className="text-secondary">Amount recovered</span>
                    <Input
                      value={draftValue('claim_amount_recovered')}
                      onChange={(e) => setDraft('claim_amount_recovered', e.target.value)}
                      onBlur={() => commitDraft('claim_amount_recovered')}
                      disabled={!editable}
                      placeholder="0.00"
                    />
                  </label>
                </div>
              </>
            ) : (
              <p className="m-0 text-sm text-tertiary">No claim filed for this case.</p>
            )}
          </section>

          {showResolutionFields ? (
            <section className="flex flex-col gap-2">
              <h3 className="m-0 text-sm font-semibold text-primary">Resolution</h3>
              <label className="flex flex-col gap-1 text-sm">
                <span className="text-secondary">Resolution type</span>
                <Select
                  value={c.resolution_type ?? ''}
                  disabled={!editable}
                  onChange={(e) =>
                    patch({ resolution_type: (e.target.value as ResolutionType) || null })
                  }
                >
                  <option value="">Not yet decided</option>
                  {ALL_RESOLUTION_TYPES.map((r) => (
                    <option key={r} value={r}>
                      {RESOLUTION_LABELS[r]}
                    </option>
                  ))}
                </Select>
              </label>
              {/* Claim amounts moved to the Claim section — they were
                  gated on resolution_type here, which left a carrier
                  claim with nowhere to record them. */}
              <label className="flex flex-col gap-1 text-sm">
                <span className="text-secondary">Resolution notes</span>
                <Textarea
                  value={draftValue('resolution_notes')}
                  onChange={(e) => setDraft('resolution_notes', e.target.value)}
                  onBlur={() => commitDraft('resolution_notes')}
                  disabled={!editable}
                  rows={3}
                  placeholder="Odoo reference / how this was actually resolved…"
                />
              </label>
            </section>
          ) : null}

          <section className="flex flex-col gap-2">
            <h3 className="m-0 text-sm font-semibold text-primary">Linked records</h3>
            {c.warehouse_incident_id ? (
              <a
                href={`/warehouse-incidents/${c.warehouse_incident_id}`}
                target="_blank"
                rel="noreferrer"
                className="flex items-center gap-1.5 text-sm text-link hover:underline"
              >
                <ExternalLink size={12} /> Warehouse Incident Report
              </a>
            ) : (
              <p className="m-0 text-sm text-tertiary">No linked incident report.</p>
            )}
            {c.inventory_discrepancy_report_id ? (
              <a
                href={`/inventory-discrepancy-reports/${c.inventory_discrepancy_report_id}`}
                target="_blank"
                rel="noreferrer"
                className="flex items-center gap-1.5 text-sm text-link hover:underline"
              >
                <ExternalLink size={12} /> Inventory Discrepancy Report
              </a>
            ) : (
              <p className="m-0 text-sm text-tertiary">No linked discrepancy report.</p>
            )}
            {c.odoo_po_name ? (
              <div className="text-sm text-secondary">PO: {c.odoo_po_name}</div>
            ) : null}
          </section>

          <section className="flex flex-col gap-2">
            <h3 className="m-0 text-sm font-semibold text-primary">Root cause</h3>
            <Textarea
              value={c.root_cause_notes ?? ''}
              disabled
              rows={3}
              placeholder="No root cause notes recorded."
            />
          </section>

          <section className="flex flex-col gap-2">
            <h3 className="m-0 text-sm font-semibold text-primary">History</h3>
            <StageHistoryTimeline entries={c.stage_history} />
          </section>
        </div>
      )}
    </Drawer>
  )
}
